import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { FireUser } from '../firestore-models/user';
import { ApiUser } from '../api-client-module';
import { ApiSetUsersRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';
import { SessionService } from '../session.service';

import { UserDialogComponent } from './user-dialog/user-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css'],
  standalone: false,
})
export class UserComponent implements OnInit, OnDestroy {

  private usersCollection!: AngularFirestoreCollection<FireUser>;
  usersObservable!: Observable<FireUser[]>;
  usersSubscribe!: Subscription;

  loginUser!: string;
  loginUserType!: number;
  loginUserProjectIds!: Array<any>;

  displayedColumns: string[] = ['position', 'edit', 'select', 'user_id', 'name', 'desc', 'type'];
  dataSource = new MatTableDataSource<FireUser>();
  selection = new SelectionModel<FireUser>(true, []);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  currentLang: string = 'ja';

  constructor(
    public dialog: MatDialog,
    private sessionService: SessionService,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    if (this.sessionService.session.isLogin) {
      this.loginUser = this.sessionService.session.user!.user_id;
      this.loginUserType = this.sessionService.session.user!.user_type;
    }
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.usersCollection = this.angularFirestore.collection<FireUser>(
      'users', ref => ref.orderBy('user_id', 'asc'));
    this.usersObservable = this.usersCollection.valueChanges({ idField: 'id' });
    this.usersSubscribe = this.usersObservable.subscribe(users => {
      // アドミンユーザーは全てのユーザー情報を取得
      if (this.loginUserType == AppConst.MANAGE_USER_TYPE_ADMIN) {
        this.dataSource.data = users;
      // プロジェクト管理ユーザーは自分のプロジェクトの一般ユーザー情報を取得
      } else if (this.loginUserType == AppConst.MANAGE_USER_TYPE_MASTER) {
        var projectIds = null;
        for (let user of users) {
          if (this.loginUser == user.user_id) {
            if (user.user_type == AppConst.MANAGE_USER_TYPE_MASTER) {
              this.loginUserProjectIds = user.project_ids;
            }
          }
        }
        if (this.loginUserProjectIds && this.loginUserProjectIds.length > 0) {
          let selfUsers = [];
          for (let user of users) {
            if (user.user_type == AppConst.MANAGE_USER_TYPE_USER) {
              for (let project_id of user.project_ids) {
                if (this.loginUserProjectIds.includes(project_id)) {
                  selfUsers.push(user);
                  break;
                }
              }
            }
          }
          this.dataSource.data = selfUsers;
        } else {
          console.warn('not found loginUserProjectIds');
          this.loginUserProjectIds = [];
        }
      }
    });
    this.currentLang = this.translate.currentLang;
  }

  ngOnDestroy(): void {
    this.usersSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: FireUser): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  openEditDialog(user: FireUser): void {
    let apiUser: ApiUser = {
      user_id: user.user_id,
      user_type: user.user_type,
      is_edit: user.is_edit,
      user_name: user.user_name,
      user_desc: user.user_desc,
      project_ids: user.project_ids,
      exhibit_ids: user.exhibit_ids
    };
    const dialogRef = this.dialog.open(UserDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, user: apiUser, login_user_project_ids: this.loginUserProjectIds }
    });
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, user: null, login_user_project_ids: this.loginUserProjectIds }
    });
  }

  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedUsers: Array<ApiUser> = new Array<ApiUser>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedUser: ApiUser = {
            user_id: row.user_id,
            user_type: row.user_type,
            is_edit: row.is_edit,
            user_name: row.user_name,
            user_desc: row.user_desc,
            project_ids: row.project_ids
          };
          selectedUsers.push(selectedUser);
        }
      }
      if (!!result) {
        let request: ApiSetUsersRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          users: selectedUsers
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setUsers(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'))
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'))
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'user_id':
          return compare(a.user_id, b.user_id, isAsc);
        case 'name':
          return compare(a.user_name, b.user_name, isAsc);
        case 'desc':
          return compare(a.user_desc, b.user_desc, isAsc);
        case 'type':
          return compare(a.user_type, b.user_type, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
